import { useState } from 'react';
import config from '../config/config';
import authHeader from '../config/authHeader';
import { useAuth } from '../contexts/AuthContext';

const useFetchDeclarations = () => {
    const { token, logout } = useAuth();

    const [declarations, setDeclarations] = useState([]);
    const [countDeclarations, setCountDeclarations] = useState(0);
    const [declaration, setDeclaration] = useState(null);
    const [siteDeclarations, setDeclarationsBySite] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchDeclarations = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getDeclarations`, {
                method: 'GET',
                headers: authHeader(),
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) throw new Error('Erreur réseau pour récupérer les déclarations');

            const data = await response.json();
            setDeclarations(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCountDeclarations = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getCountDeclarations`, {
                method: 'GET',
                headers: authHeader(),
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) throw new Error('Erreur réseau pour récupérer le compteur des déclarations');

            const { count } = await response.json();
            setCountDeclarations(count);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDeclarationByUUID = async (uuid) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getDeclarationByUUID?uuid=${uuid}`, {
                method: 'GET',
                headers: authHeader(),
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) throw new Error('Erreur réseau pour récupérer la déclaration par UUID');

            const data = await response.json();
            setDeclaration(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDeclarationsBySiteUUID = async (siteUUID) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getDeclarationsBySiteUUID?uuid=${siteUUID}`, {
                method: 'GET',
                headers: authHeader(),
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) throw new Error('Erreur réseau pour récupérer les déclarations par site UUID');

            const data = await response.json();
            setDeclarationsBySite(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDeclarationCalculDommage = async (declarationUUID) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_DeclarationCalculDommage?uuid=${declarationUUID}`, {
                method: 'GET',
                headers: authHeader(),
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) throw new Error('Erreur réseau pour récupérer les calculs de dommages');

            await response.json();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchUpdateDeclaration = async (declaration) => {
        setLoading(true);
        setError(null);

        const formData = new FormData();
        for (const key in declaration) {
            if (declaration.hasOwnProperty(key)) {
                formData.append(key, declaration[key]);
            }
        }

        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_editDeclaration`, {
                method: 'POST',
                headers: authHeader(),
                body: formData,
            });

            if (response.status === 401) {
                logout();
                return null;
            }

     if (!response.ok) {
            const message =`Erreur (${response.status}) lors de la mise à jour de la déclaration`;

            const err = new Error(message);
            err.status = response.status;
            throw err;
        }
            const data = await response.json();
            setDeclaration(data);
        } catch (err) {
            setError(err.message);
            throw err
        } finally {
            setLoading(false);
        }
    };

    const fetchInvitationsDeclarations = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getInvitationsDeclarations`, {
                method: 'GET',
                headers: authHeader(),
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) throw new Error('Erreur réseau pour récupérer les invitations de déclarations');

            const data = await response.json();
            setDeclarations(data);
        }
        catch (err) {
            setError(err.message);
            return null;
        }
        finally {
            setLoading(false);
        }
    };

    const createDeclaration = async (declaData) => {
        setLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append("declaPayload", JSON.stringify(declaData));

        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_createDeclaration`, {
                method: 'POST',
                headers: authHeader(),
                body: formData,
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) {
                throw new Error('Erreur réseau lors de la création de la declaration');
            }

            const data = await response.json();
            return data;
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    };
    const updateDeclaration = async (declaData) => {
        setLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append("declaPayload", JSON.stringify(declaData));

        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_updateDeclaration`, {
                method: 'POST',
                headers: authHeader(),
                body: formData,
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) {
                const message = `Erreur (${response.status}) lors de la mise à jour de la déclaration`;

                const err = new Error(message);
                err.status = response.status;
                throw err;
            }

            const data = await response.json();
            return data;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        declarations,
        countDeclarations,
        declaration,
        siteDeclarations,
        fetchDeclarations,
        fetchCountDeclarations,
        fetchDeclarationByUUID,
        fetchDeclarationsBySiteUUID,
        fetchDeclarationCalculDommage,
        fetchUpdateDeclaration,
        fetchInvitationsDeclarations,
        createDeclaration,
        updateDeclaration,
        loading,
        error,
    };
};

export default useFetchDeclarations;
